/**
 * COSA VALE DAVVERO PER L'AZIENDA, E DA DOVE ARRIVA — `GET /api/tenant-config/effective`.
 *
 * Dal BE v1.248.0 (task 1900) il lucchetto d'area **porta il valore**: una figlia bloccata legge la
 * riga di chi l'ha bloccata, una libera legge la propria, poi quella dell'antenato più vicino, poi
 * il default. Le righe di `tenant-attributes` dell'azienda quindi non dicono più cosa vale: con il
 * lucchetto chiuso la riga della figlia resta e **non conta**. Mostrarla come valore vuol dire far
 * vedere una cosa e farne applicare un'altra.
 *
 * Questa lettura è l'unico posto che lo sa, perché usa lo stesso predicato del `403
 * TENANT_AREA_LOCKED`. Copre solo le chiavi governate da un'area: gli interruttori dei moduli
 * (`<modulo>.enabled`) e le chiavi fuori area (`portal.`, `maespace.`…) restano dell'azienda, e
 * per quelle vale la riga di `tenant-attributes`.
 *
 * Risposta semplice, non JSON:API: `{ tenantId, attributes, tenantFields }`. Claim
 * `TenantAttribute.read`, lo stesso di `tenant-attributes`. Contratto:
 * `maefwk10/docs/contract/BE-RESPONSE-TO-FE-EREDITARIETA-REGOLE-HR-E-SEDE-v1.md` §3.
 */
import { useQuery } from '@tanstack/react-query';
import { httpRequest } from '@/shared/api/client';

/** `tenant` = riga propria · `holding` = di un'azienda sopra · `default` = nessuno in catena. */
export type EffectiveLevel = 'tenant' | 'holding' | 'default';

export interface EffectiveAttribute {
  key: string;
  area: string | null;
  /** Il valore che il server applica. */
  value: string | null;
  level: EffectiveLevel;
  sourceTenantId: string | null;
  sourceTenantName: string | null;
  /** L'azienda ha una riga sua: con il lucchetto chiuso c'è, e non conta. */
  hasOwnRow: boolean;
  ownValue: string | null;
  catalogDefault: string | null;
  isLocked: boolean;
  lockedByTenantId: string | null;
  lockedByTenantName: string | null;
}

export interface EffectiveTenantField {
  area: string;
  field: string;
  isRelationship: boolean;
  inheritsWhenEmpty: boolean;
  value: unknown;
  ownValue: unknown;
  level: EffectiveLevel;
  sourceTenantId: string | null;
  sourceTenantName: string | null;
  isLocked: boolean;
  lockedByTenantId: string | null;
  lockedByTenantName: string | null;
}

export interface EffectiveConfig {
  tenantId: string;
  attributes: EffectiveAttribute[];
  tenantFields: EffectiveTenantField[];
}

export const EFFECTIVE_CONFIG_KEY = ['tenant-config-effective'] as const;

export async function fetchEffectiveConfig(tenantId?: string | null): Promise<EffectiveConfig> {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return httpRequest<EffectiveConfig>(`/api/tenant-config/effective${qs}`, { endpointType: 'plain' });
}

/**
 * La lettura, una per azienda. Senza `tenantId` è l'azienda del token. Quando non risponde
 * (permesso mancante, server vecchio) chi la usa ripiega sulle righe proprie: è quel che si
 * mostrava prima, e non è peggio.
 */
export function useEffectiveConfig(tenantId?: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...EFFECTIVE_CONFIG_KEY, tenantId ?? 'self'],
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => fetchEffectiveConfig(tenantId),
  });
}

/** Le chiavi in una mappa, per chi le cerca una alla volta. */
export function effectiveByKey(config: EffectiveConfig | undefined): Map<string, EffectiveAttribute> {
  const m = new Map<string, EffectiveAttribute>();
  for (const a of config?.attributes ?? []) m.set(a.key, a);
  return m;
}

/** Le colonne dell'azienda in una mappa, per nome pubblico del campo. */
export function effectiveFieldByName(config: EffectiveConfig | undefined): Map<string, EffectiveTenantField> {
  const m = new Map<string, EffectiveTenantField>();
  for (const f of config?.tenantFields ?? []) m.set(f.field, f);
  return m;
}

/**
 * Da dove viene il valore, scritto per chi guarda. `null` quando non c'è niente da dire: il valore
 * è della propria azienda e nessuno sopra lo blocca, oppure è il default e non c'è una riga.
 *
 * I casi che contano sono due, e sono quelli che a schermo ingannano:
 * - il valore è di un'altra azienda, perché la propria non ha la riga o perché è bloccata;
 * - la propria riga c'è e **non conta**, perché la capogruppo ha chiuso il lucchetto.
 */
export function originNote(entry: { level: EffectiveLevel; sourceTenantName: string | null; hasOwnRow?: boolean;
  isLocked: boolean; ownValue?: unknown; value?: unknown }, shown?: string | null): string | null {
  const from = entry.sourceTenantName ?? 'la capogruppo';
  /* `shown`: il valore già scritto per chi legge. Serve dove il comando mostra la riga propria e
     non quella che vale — le colonne dell'azienda nella scheda. */
  const what = shown ? ` («${shown}»)` : '';
  if (entry.level === 'holding') {
    if (entry.isLocked) {
      const differs = (entry.hasOwnRow ?? entry.ownValue != null)
        && String(entry.ownValue ?? '') !== String(entry.value ?? '');
      return differs
        ? `Vale il valore di ${from}${what}. Questa azienda ne ha uno suo, che finché il lucchetto resta chiuso non conta.`
        : `Vale il valore di ${from}${what}.`;
    }
    return `Questa azienda non ha un valore suo: vale quello di ${from}${what}. Cambiandolo, ne scrive uno suo.`;
  }
  return null;
}
